import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { formatSlackContent } from '../../../utils/utils';
import { SharedTenantService } from '../../super-admin-taxonomy/shared-tenant.service';

interface KnowledgeGapTopicDetail {
  id: string;
  conversationId: string;
  topicTitle: string;
  topicDescription: string;
  context: string;
  confidence: number;
  createdAt: string;
  updatedAt: string;
  insight?: {
    id: string;
    category: string;
    language: string;
    projectId?: string;
  };
  conversation?: {
    id: string;
    friendlyId: string;
    status: string;
    summary: string;
    createdAt: string;
    closedAt?: string;
    channelId: string;
  };
}

@Component({
  selector: 'app-knowledge-gap-topic-detail',
  templateUrl: './knowledge-gap-topic-detail.component.html',
  styleUrls: ['./knowledge-gap-topic-detail.component.scss'],
})
export class KnowledgeGapTopicDetailComponent implements OnInit {
  topic: KnowledgeGapTopicDetail | null = null;
  tenantId: string | null = null;
  topicId: string | null = null;
  selectedTenant: Partial<Tenant> | null = null;
  isLoading = false;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private messageService: MessageService,
    private sharedTenantService: SharedTenantService,
    private tenantsService: TenantsService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.tenantId = params.get('tenantId');
      this.topicId = params.get('topicId');
      if (this.tenantId && this.topicId) {
        this.loadTenantAndTopic();
      }
    });
  }

  async loadTenantAndTopic() {
    if (!this.tenantId || !this.topicId) return;

    // Try to get tenant from shared service first
    const sharedTenant = this.sharedTenantService.getSelectedTenant();
    if (sharedTenant && sharedTenant.id === this.tenantId) {
      this.selectedTenant = sharedTenant;
      this.loadTopic();
    } else {
      // Fetch tenant data directly from API to get subdomain
      try {
        const tenant = await this.tenantsService.getTenantById(this.tenantId);
        this.selectedTenant = tenant;
        this.sharedTenantService.setSelectedTenant(tenant);
        this.loadTopic();
      } catch (error) {
        this.error = 'Failed to load tenant: ' + (error.message || 'Unknown error');
      }
    }
  }

  async loadTopic() {
    if (!this.tenantId || !this.topicId) return;

    this.isLoading = true;
    this.error = null;

    try {
      // Note: This endpoint doesn't exist yet, we'll need to create it
      const response = (await this.apiService.getPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/topics/${this.topicId}`,
      )) as KnowledgeGapTopicDetail;

      this.topic = response;
    } catch (error) {
      this.error = 'Failed to load topic: ' + (error.message || 'Unknown error');
    } finally {
      this.isLoading = false;
    }
  }

  goBack() {
    this.router.navigate(['/super-admin/knowledge-gaps', this.tenantId]);
  }

  getInsightUrl(insightId: string): string {
    if (!this.tenantId) return '#';
    return `/super-admin/knowledge-gaps/${this.tenantId}/insights/${insightId}`;
  }

  getConversationUrl(conversationId: string): string {
    if (!this.selectedTenant?.subdomain) {
      return '#';
    }

    // Generate URL to tenant's subdomain conversation page
    const currentUrl = new URL(window.location.href);

    // For dev environment, extract base domain properly
    // Current hostname might be: unthread.unthread.localhost:5002
    // We want: unthread.localhost:5002 (skip the first subdomain)
    const hostnameParts = currentUrl.hostname.split('.');
    let baseDomain: string;

    if (hostnameParts.length > 2) {
      // Skip the first part (current tenant subdomain) and use the rest
      baseDomain = hostnameParts.slice(1).join('.');
    } else {
      baseDomain = currentUrl.hostname;
    }

    // Include port if it exists
    const port = currentUrl.port ? `:${currentUrl.port}` : '';
    return `${currentUrl.protocol}//${baseDomain}${port}/dashboard/conversations/${conversationId}`;
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getConfidenceLabel(score: number): string {
    if (score >= 0.8) return 'High';
    if (score >= 0.6) return 'Medium';
    return 'Low';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getConversationTitle(): string {
    if (!this.topic) return 'Unknown Conversation';

    // Use conversation summary if available
    if (this.topic.conversation?.summary && this.topic.conversation.summary.trim()) {
      return this.topic.conversation.summary.trim();
    }

    // Fall back to topic title
    if (this.topic.topicTitle && this.topic.topicTitle.trim()) {
      return this.topic.topicTitle.trim();
    }

    // Final fallback
    return `Conversation ${this.topic.conversationId.substring(0, 8)}...`;
  }
}
